import { FC } from "react";
import { Form, Select } from "antd";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { DeleteIcon, AddIcon } from "src/assets/Icons";
import { Stepper } from "../Stepper/Stepper";

type GuestsSelectionContentProps = {
  className?: string;
  listClassName?: string;
};

const GuestsSelectionContent: FC<GuestsSelectionContentProps> = ({ className, listClassName }) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();

  return (
    <Form.List name="rooms">
      {(fields, { add, remove }) => (
        <div className={cn("flex flex-col gap-3", className)}>
          <div className={cn("flex flex-col gap-3", listClassName && "overflow-y-auto", listClassName)}>
            {fields.map(({ key, name }) => {
              const childrenCount = form.getFieldValue(["rooms", name, "children"]) || 0;
              return (
                <div key={key} className="border-border-hover flex flex-col rounded-md border bg-background-default p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-font-default text-sm font-bold">{t("Hotels.search.roomNumber", { number: name + 1 })}</span>
                    {fields.length > 1 && (
                      <button type="button" className="text-icon-subtle hover:text-font-danger" onClick={() => remove(name)}>
                        <DeleteIcon />
                      </button>
                    )}
                  </div>

                  <div className="border-border-minimal flex items-center justify-between border-b py-3">
                    <div>
                      <p className="text-font-default text-sm font-semibold">{t("Flights.search.adults")}</p>
                      <p className="text-font-subtle text-12">{t("Hotels.search.adultsAgeRangeSeventeen")}</p>
                    </div>
                    <Form.Item name={[name, "adults"]} className="m-0!">
                      <Stepper min={1} max={6} />
                    </Form.Item>
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-font-default text-sm font-semibold">{t("Flights.search.children")}</p>
                      <p className="text-font-subtle text-12">{t("Hotels.search.childrenAgeRangeZeroToSeventeen")}</p>
                    </div>
                    <Form.Item name={[name, "children"]} className="m-0!">
                      <Stepper
                        min={0}
                        max={4}
                        onChange={(val) => {
                          const currentAges: number[] = form.getFieldValue(["rooms", name, "childrenAges"]) || [];
                          const childrenAges =
                            val < currentAges.length
                              ? currentAges.slice(0, val)
                              : val > currentAges.length
                                ? [...currentAges, ...Array(val - currentAges.length).fill(0)]
                                : currentAges;
                          form.setFieldValue(["rooms", name, "childrenAges"], childrenAges);
                        }}
                      />
                    </Form.Item>
                  </div>

                  {childrenCount > 0 && (
                    <div className="bg-background-informative flex flex-col gap-2 rounded-md p-3">
                      <span className="text-font-default text-12 font-semibold">{t("Hotels.search.childrenAges")}</span>
                      <div className="grid grid-cols-2 gap-2">
                        {Array.from({ length: childrenCount }).map((_, i) => (
                          <Form.Item key={`${name}-age-${i}`} name={[name, "childrenAges", i]} className="m-0!">
                            <Select
                              className="w-full"
                              placeholder={t("Hotels.search.childAgePlaceholder", { number: i + 1 })}
                              size="small"
                              options={Array.from({ length: 18 }, (_, idx) => ({
                                value: idx,
                                label: t("Hotels.search.yearsShort", { count: idx }),
                              }))}
                            />
                          </Form.Item>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button
            type="button"
            disabled={fields.length >= 5}
            className={cn(
              "flex items-center justify-center gap-2 rounded-md border-2 border-dashed py-3 text-sm font-semibold",
              fields.length >= 5
                ? "border-border-disabled text-font-disabled cursor-not-allowed"
                : "border-font-active text-font-active",
            )}
            onClick={() => add({ adults: 1, children: 0, childrenAges: [] })}
          >
            <AddIcon />
            {t("Hotels.search.addAnotherRoom")}
          </button>
        </div>
      )}
    </Form.List>
  );
};

export default GuestsSelectionContent;
